import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { AuthService } from './auth.service';

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) :boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const claims = route.data['claimCodes'] as Array<string>;

  const resolveSuccess = (): boolean | UrlTree => {
    const claimsResult = authService.hasClaims(claims, true);
    if (claimsResult instanceof Observable) {
      return false; // Fallback
    }
    if (!claimsResult) {
      return false;
    }
    // Strip auto-login params from the URL if they were used
    if (route.queryParams['userId'] != null && route.queryParams['incidentId'] != null) {
      const { userId, incidentId, ...remainingParams } = route.queryParams;
      const path = state.url.split('?')[0];
      return router.createUrlTree([path], { queryParams: remainingParams });
    }
    return true;
  };
  
  if (authService.isLoggedIn()) {
    // Check if current token is expired
    if (authService.isCurrentTokenExpired()) {
      // Token expired, need to refresh
      return authService.refreshToken(route.params, route.queryParams).pipe(
        map(success => {
          if (success) {
            return resolveSuccess();
          } else {
            const retVal = router.createUrlTree(['/login']);
            retVal.queryParams = {"returnUrl": state.url};
            return retVal;
          }
        })
      );
    }
    // Token is valid, check claims
    const hasClaimsResult = authService.hasClaims(claims, true);
    if (hasClaimsResult instanceof Observable) {
      return hasClaimsResult;
    }
    return hasClaimsResult as boolean;
  } else {
    const storedLoginResult = authService.getStoredLogin(route.params, route.queryParams);
    
    // Handle both boolean and Observable return types
    if (storedLoginResult instanceof Observable) {
      return storedLoginResult.pipe(
        map(success => {
          if (success) {
            return resolveSuccess();
          } else {
            const retVal = router.createUrlTree(['/login']);
            retVal.queryParams = {"returnUrl": state.url};
            return retVal;
          }
        })
      );
    } else if (storedLoginResult) {
      // Token was valid, check claims
      const hasClaimsResult = authService.hasClaims(claims, true);
      if (hasClaimsResult instanceof Observable) {
        return hasClaimsResult;
      }
      return hasClaimsResult as boolean;
    } else {
      // No stored login, redirect to login
      const retVal = router.createUrlTree(['/login']);
      retVal.queryParams = {"returnUrl": state.url};
      return retVal;
    }
  }
};
